import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { handleInvalidSession } from "@/lib/session-guard";

const SESSION_CHANGE_EVENT = "rpgcoin_session_change";

const SESSION_KEY = "rpgcoin_client_session";

export interface ClientSession {
  contato: string;
  token: string;
  canal: "whatsapp" | "email" | "senha";
  apelido: string | null;
  avatar_url: string | null;
  nome: string | null;
  perfil_id: string | null;
}

import { normalizePhone as sharedNormalizePhone } from "@/lib/phone-utils";

const normalizeContato = (contato: string, canal: ClientSession["canal"]) => {
  const value = (contato || "").trim();
  // canal "senha" can be either email or phone — detect by format.
  // canal "email" is always email; "whatsapp" is always phone.
  const isEmail = canal === "email" || (canal === "senha" && value.includes("@"));
  if (isEmail) return value.toLowerCase();
  return sharedNormalizePhone(value);
};

const sanitizeSession = (data: ClientSession): ClientSession | null => {
  const token = String(data?.token || "").trim();
  const canal = data?.canal;

  if (!data?.contato || !token || !canal) return null;

  // Legacy OTP sessions (5-digit code via WhatsApp/email) should not be reused as long sessions.
  if ((canal === "whatsapp" || canal === "email") && /^\d{5}$/.test(token)) {
    return null;
  }

  return {
    ...data,
    contato: normalizeContato(data.contato, canal),
    token,
  };
};

export const useClientSession = () => {
  const [session, setSession] = useState<ClientSession | null>(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as ClientSession;
      const sanitized = sanitizeSession(parsed);

      if (!sanitized) {
        localStorage.removeItem(SESSION_KEY);
        return null;
      }

      if (JSON.stringify(parsed) !== JSON.stringify(sanitized)) {
        localStorage.setItem(SESSION_KEY, JSON.stringify(sanitized));
      }

      return sanitized;
    } catch {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
  });

  const validatedRef = useRef(false);

  // Validate session token on first load (check if expired server-side)
  useEffect(() => {
    if (!session || validatedRef.current) return;
    validatedRef.current = true;

    supabase.functions
      .invoke("client-profile", {
        body: { contato: session.contato, token: session.token },
      })
      .then(({ data, error }) => {
        // Only clear session on explicit token rejection (401 / "Token inválido")
        // Generic errors (500, timeout, network) should NOT logout the user
        if (error) {
          const msg = String(error?.message || error || "");
          if (msg.includes("401") || msg.includes("Token inválido") || msg.includes("Sessão inválida")) {
            handleInvalidSession();
            setSession(null);
          }
          // else: server error / timeout — keep session
          return;
        }
        if (data?.error === "Token inválido" || data?.error === "Sessão inválida") {
          handleInvalidSession();
          setSession(null);
          return;
        }
        // Note: do NOT clear session if profile is missing without an explicit error.
        // The user may not have completed registration yet, but the token is still valid.
      })
      .catch(() => {
        // Network error — keep session, don't break UX
      });
  }, [session?.contato, session?.token]);

  // Sync across hook instances within the same tab
  useEffect(() => {
    const handler = () => {
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) {
          setSession(null);
          return;
        }
        const parsed = JSON.parse(raw) as ClientSession;
        const sanitized = sanitizeSession(parsed);
        setSession(sanitized);
      } catch {
        setSession(null);
      }
    };
    window.addEventListener(SESSION_CHANGE_EVENT, handler);
    return () => window.removeEventListener(SESSION_CHANGE_EVENT, handler);
  }, []);

  const notifyChange = () => {
    setTimeout(() => window.dispatchEvent(new Event(SESSION_CHANGE_EVENT)), 0);
  };

  const login = useCallback((data: ClientSession) => {
    const sanitized = sanitizeSession(data);
    if (!sanitized) {
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      notifyChange();
      return;
    }

    localStorage.setItem(SESSION_KEY, JSON.stringify(sanitized));
    setSession(sanitized);
    validatedRef.current = true; // Just logged in, no need to re-validate
    notifyChange();
  }, []);

  const logout = useCallback(() => {
    const current = session;
    // Clear local state immediately
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    validatedRef.current = false;
    notifyChange();

    // Invalidate server token (fire-and-forget)
    if (current?.contato && current?.token) {
      supabase.functions
        .invoke("client-logout", {
          body: { contato: current.contato, token: current.token },
        })
        .catch(() => {});
    }
  }, [session]);

  const updateProfile = useCallback((updates: Partial<ClientSession>) => {
    setSession((prev) => {
      if (!prev) return null;
      const next = { ...prev, ...updates };
      const sanitized = sanitizeSession(next);
      if (!sanitized) {
        localStorage.removeItem(SESSION_KEY);
        notifyChange();
        return null;
      }
      localStorage.setItem(SESSION_KEY, JSON.stringify(sanitized));
      notifyChange();
      return sanitized;
    });
  }, []);

  return { session, login, logout, updateProfile };
};
